import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, CardActions, IconButton, Stack, Chip, CircularProgress, Tooltip, DialogTitle, DialogContent, TextField, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import { Edit as EditIcon, Security as SecurityIcon, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import DialogWrapper from '@/components/DialogWrapper';
import { useCurrentUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import {
  AdminAgent,
  useAdminAgentsQuery,
  useCreateAgentMutation,
  useDeleteAgentMutation,
  useToggleAgentActiveMutation,
  useToggleAgentImplicitMutation,
  useUpdateAgentMutation,
} from '@/features/admin/hooks/useAdminResourcesQuery';

type StaticAgent = AdminAgent;

const FALLBACK_AGENTS: StaticAgent[] = [];

const AgentsTab: React.FC = () => {
  const { isPending: isUserSettingsPending, refetch: refetchCurrentUserSettings } = useCurrentUserSettingsQuery();
  const {
    data: queriedAgents,
    isPending: isAgentsPending,
    refetch: refetchAgents,
  } = useAdminAgentsQuery();
  const createAgentMutation = useCreateAgentMutation();
  const updateAgentMutation = useUpdateAgentMutation();
  const deleteAgentMutation = useDeleteAgentMutation();
  const toggleAgentActiveMutation = useToggleAgentActiveMutation();
  const toggleAgentImplicitMutation = useToggleAgentImplicitMutation();
  const [agents, setAgents] = useState<StaticAgent[]>(FALLBACK_AGENTS);
  const [updating, setUpdating] = useState<string | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentDescription, setNewAgentDescription] = useState('');
  const [newAgentIcon, setNewAgentIcon] = useState('');
  const [addingAgent, setAddingAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState<StaticAgent | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<StaticAgent | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!queriedAgents) {
      return;
    }

    setAgents(queriedAgents);
  }, [queriedAgents]);

  const fetchSettings = async () => {
    try {
      await refetchCurrentUserSettings();
    } catch (e) {
      console.error('Failed to fetch user settings', e);
    }
  };

  const handleRefresh = async () => {
    await Promise.allSettled([refetchCurrentUserSettings(), refetchAgents()]);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleToggleActive = async (agent: StaticAgent) => {
    try {
      await toggleAgentActiveMutation.mutateAsync(agent.id);
      showSnackbar(`Agent ${agent.is_active ? 'deactivated' : 'activated'} successfully`, 'success');
    } catch (e) {
      showSnackbar('Failed to toggle agent status', 'error');
    }
  };

  const handleToggleImplicit = async (agent: StaticAgent) => {
    try {
      await toggleAgentImplicitMutation.mutateAsync(agent.id);
      showSnackbar(`Agent access changed to ${agent.is_implicit ? 'explicit' : 'implicit'}`, 'success');
    } catch (e) {
      showSnackbar('Failed to toggle agent implicit status', 'error');
    }
  };

  const openEditAgentDialog = (agent: StaticAgent) => {
    setEditingAgent(agent);
    setNewAgentName(agent.name);
    setNewAgentDescription(agent.description || '');
    setNewAgentIcon(agent.icon || '');
    setOpenEditDialog(true);
  };

  const handleEditAgent = async () => {
    if (!editingAgent) return;
    try {
      await updateAgentMutation.mutateAsync({
        agentId: editingAgent.id,
        payload: {
        name: newAgentName, 
        description: newAgentDescription,
        icon: newAgentIcon
        },
      });
      showSnackbar('Agent updated successfully', 'success');
      setOpenEditDialog(false);
      setEditingAgent(null);
      setNewAgentName('');
      setNewAgentDescription('');
      setNewAgentIcon('');
    } catch (e) {
      showSnackbar('Failed to update agent', 'error');
    }
  };

  const openDeleteAgentDialog = (agent: StaticAgent) => {
    setDeletingAgent(agent);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAgent = async () => {
    if (!deletingAgent) return;
    try {
      await deleteAgentMutation.mutateAsync(deletingAgent.id);
      showSnackbar('Agent deleted successfully', 'success');
      setOpenDeleteDialog(false);
      setDeletingAgent(null);
    } catch (e) {
      showSnackbar('Failed to delete agent', 'error');
    }
  };

  const loading = isUserSettingsPending || isAgentsPending;
  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;

  return (
    <>
      <Box sx={{ width: '100%', pl: 2, pr: 2 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" color="primary" onClick={handleRefresh}><RefreshIcon fontSize="small" /></IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label="add-agent" size="small" color="primary" onClick={() => { setOpenAddDialog(true); }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(360px, 1fr))' }, gap: 2 }}>
          {agents.map(agent => (
            <Card key={agent.id}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{agent.name}</Typography>
                {agent.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{agent.description}</Typography>}
                {agent.icon && <Typography variant="body2" color="text.secondary">{agent.icon}</Typography>}
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title={agent.is_active ? 'Deactivate' : 'Activate'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(agent)}
                        sx={{ color: agent.is_active ? 'green' : 'rgba(0,0,0,0.6)' }}
                        aria-label={agent.is_active ? 'deactivate' : 'activate'}
                      >
                        {agent.is_active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={agent.is_implicit ? 'Make Explicit' : 'Make Implicit'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleImplicit(agent)}
                        sx={{ color: agent.is_implicit ? '#1976d2' : 'rgba(0,0,0,0.6)' }}
                        aria-label={agent.is_implicit ? 'make-explicit' : 'make-implicit'}
                      >
                        <SecurityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit Agent">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => openEditAgentDialog(agent)}
                        sx={{ color: '#1976d2' }}
                        aria-label="edit-agent"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete Agent">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteAgentDialog(agent)}
                        sx={{ color: '#d32f2f' }}
                        aria-label="delete-agent"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Box>
                  <Chip 
                    label={agent.is_implicit ? 'Implicit' : 'Explicit'} 
                    sx={{ 
                      backgroundColor: agent.is_implicit ? '#4CAF50' : '#FF9800', 
                      color: 'white', 
                      fontSize: '0.75rem' 
                    }} 
                    size="small" 
                  />
                </Box>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>

      <DialogWrapper open={openAddDialog} onClose={() => { if (!addingAgent) { setOpenAddDialog(false); setNewAgentId(''); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Agent
          <IconButton
            aria-label="close"
            onClick={() => { setOpenAddDialog(false); setNewAgentId(''); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField autoFocus margin="normal" label="Agent ID (unique)" fullWidth variant="outlined" required value={newAgentId} onChange={(e) => setNewAgentId(e.target.value)} />
            <TextField margin="normal" label="Agent name" fullWidth variant="outlined" value={newAgentName} onChange={(e) => setNewAgentName(e.target.value)} />
            <TextField margin="normal" label="Icon (emoji)" fullWidth variant="outlined" value={newAgentIcon} onChange={(e) => setNewAgentIcon(e.target.value)} placeholder="🤖" />
            <TextField margin="normal" label="Description" fullWidth variant="outlined" multiline rows={2} value={newAgentDescription} onChange={(e) => setNewAgentDescription(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenAddDialog(false); setNewAgentId(''); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={async () => {
              if (!newAgentId) return;
              setAddingAgent(true);
              try {
                await createAgentMutation.mutateAsync({ id: newAgentId.trim(), name: (newAgentName || newAgentId).trim(), description: newAgentDescription.trim(), icon: newAgentIcon.trim(), is_implicit: false });
                setOpenAddDialog(false);
                setNewAgentId('');
                setNewAgentName('');
                setNewAgentDescription('');
                setNewAgentIcon('');
                showSnackbar('Agent created successfully', 'success');
              } catch (e) {
                showSnackbar('Failed to create agent', 'error');
              } finally {
                setAddingAgent(false);
              }
            }} color="primary" variant="contained" disabled={addingAgent || !newAgentId}>
              {addingAgent ? <CircularProgress size={24} /> : 'Add Agent'}
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      <DialogWrapper open={openEditDialog} onClose={() => { setOpenEditDialog(false); setEditingAgent(null); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Agent
          <IconButton
            aria-label="close"
            onClick={() => { setOpenEditDialog(false); setEditingAgent(null); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField margin="normal" label="Agent name" fullWidth variant="outlined" value={newAgentName} onChange={(e) => setNewAgentName(e.target.value)} />
            <TextField margin="normal" label="Icon (emoji)" fullWidth variant="outlined" value={newAgentIcon} onChange={(e) => setNewAgentIcon(e.target.value)} placeholder="🤖" />
            <TextField margin="normal" label="Description" fullWidth variant="outlined" multiline rows={2} value={newAgentDescription} onChange={(e) => setNewAgentDescription(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenEditDialog(false); setEditingAgent(null); setNewAgentName(''); setNewAgentDescription(''); setNewAgentIcon(''); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditAgent} color="primary" variant="contained">
              Update
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      <DialogWrapper open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setDeletingAgent(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delete Agent
          <IconButton
            aria-label="close"
            onClick={() => { setOpenDeleteDialog(false); setDeletingAgent(null); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete agent "{deletingAgent?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenDeleteDialog(false); setDeletingAgent(null); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleDeleteAgent} color="error" variant="contained">
              Delete
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AgentsTab;
